import { Delete } from "@mui/icons-material";
import {
  alpha,
  Box,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { DropzoneArea } from "react-mui-dropzone";
import { SnackbarContext } from "../../../../contexts/Snackbar";
import {
  isHeicFile,
  maxFileUploadSize,
  supportedFileTypes,
} from "../../../../enums/Consts";
import { snackbarSeverity } from "../../../../hooks/useSnackbar";
import usePrimaryColor from "../../../../hooks/usePrimaryColor";
import FileIcon from "../../../display/FileIcon";
import SimpleAlert from "../../../display/SimpleAlert";
import {
  formatFileSize,
  getFileExtension,
  getFileSignature,
} from "../utils/FileUploadFunctions";

// Scoped overrides, the global .custom-dropzone-root styles are shared with other dropzones
function getDropzoneSx(primaryColor) {
  return {
    "& .custom-dropzone-root": {
      minHeight: 200,
      border: "2px dashed",
      borderColor: "divider",
      borderRadius: 3,
      bgcolor: "action.hover",
      transition: "border-color 0.2s ease, background-color 0.2s ease",
      "&:hover": {
        borderColor: primaryColor,
        bgcolor: alpha(primaryColor, 0.04),
      },
    },
    "& .dropZoneText": {
      textDecoration: "none",
      fontSize: "1rem",
      fontWeight: 500,
      color: "text.secondary",
      px: 2,
    },
    "& .MuiDropzoneArea-icon": {
      color: primaryColor,
      width: 48,
      height: 48,
    },
  };
}

export default function AppendFilesStep({
  selectedFiles,
  onFilesSelected,
  onFileDelete,
  numberOfFileLimit = 20,
  message = "MaximumFileSize5MB",
}) {
  const { t } = useTranslation();
  const { handleSnackbar } = React.useContext(SnackbarContext);
  const primaryColor = usePrimaryColor();

  const handleDrop = (newFiles) => {
    if (newFiles && newFiles.length > 0) {
      onFilesSelected(newFiles);
    }
  };

  function handleRemove(file) {
    onFileDelete(file);
  }
  return (
    <Stack spacing={3}>
      <SimpleAlert severity={"info"} message={message} />
      <Box sx={getDropzoneSx(primaryColor)}>
        <DropzoneArea
          onDropRejected={(rejectedFiles) => {
            const file = rejectedFiles[0];
            if (isHeicFile(file)) {
              handleSnackbar("HeicNotSupportedSelectOther", snackbarSeverity.ERROR);
              return;
            }
            if (file && file.size > maxFileUploadSize) {
              handleSnackbar("FileTooLarge", snackbarSeverity.ERROR);
            } else if (file && !supportedFileTypes.includes(file.type)) {
              handleSnackbar("UnsupportedFileType", snackbarSeverity.ERROR);
            } else {
              handleSnackbar("SomethingWentWrong", snackbarSeverity.ERROR);
            }
          }}
          acceptedFiles={supportedFileTypes}
          dropzoneParagraphClass="dropZoneText"
          dropzoneClass="custom-dropzone-root"
          onChange={handleDrop}
          dropzoneText={t("DragAndDropAFile")}
          filesLimit={numberOfFileLimit}
          maxFileSize={maxFileUploadSize}
          showAlerts={false}
          showPreviewsInDropzone={false}
          initialFiles={selectedFiles}
        />
      </Box>

      {selectedFiles?.length > 0 && (
        <Box>
          <Typography
            sx={{
              mb: 1.5,
              fontWeight: 700,
              fontSize: "0.75rem",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              color: "text.secondary",
            }}
          >
            {t("SelectedFiles")} ({selectedFiles.length})
          </Typography>
          <Grid container spacing={1.5}>
            {selectedFiles.map((file) => (
              <Grid item key={getFileSignature(file)} xs={12} sm={6} md={4}>
                <Paper
                  variant="outlined"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    p: 1.25,
                    pl: 1.5,
                    borderRadius: 2,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexShrink: 0,
                      "& svg": { width: 28, height: 28 },
                    }}
                  >
                    <FileIcon fileExtension={getFileExtension(file.name)} />
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      noWrap
                      title={file.name}
                    >
                      {file.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatFileSize(file.size)}
                    </Typography>
                  </Box>
                  <IconButton
                    aria-label={`${t("Delete")} ${file.name}`}
                    title={t("Delete")}
                    size="small"
                    color="error"
                    onClick={() => handleRemove(file)}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Stack>
  );
}
